import { TrialPeriod } from 'commons/services/configurationService/configurationServiceResponses';
import { MoneyObjectInterface } from 'commons/types';

export type TrialPeriodAlertLevel =
  | 'initial'
  | 'warning'
  | 'danger'
  | 'willBeBlocked'
  | 'blocked';
export type TrialPeriodCriterion = 'days' | 'sales' | 'balance';

export interface TrialPeriodProgress {
  daysProgress: number;
  salesProgress: number;
  balanceProgress: number;
  maxProgress: number;
  leadingCriterion: TrialPeriodCriterion;
  alertLevel: TrialPeriodAlertLevel;
  remaining: {
    days: number;
    sales: number;
    balance: MoneyObjectInterface;
  };
}

const calculatePercentage = (current: number, max: number): number => {
  if (max === 0) return 0;
  return Math.min((current / max) * 100, 100);
};

const getAlertLevel = (
  progress: number,
  blockedTrialPeriod?: boolean,
): TrialPeriodAlertLevel => {
  if (blockedTrialPeriod) return 'blocked';
  if (progress >= 100) return 'willBeBlocked';
  if (progress >= 80) return 'danger';
  if (progress >= 30) return 'warning';
  return 'initial';
};

export const calculateTrialPeriodProgress = (
  trialPeriod: TrialPeriod,
  blockedTrialPeriod?: boolean,
): TrialPeriodProgress => {
  const { metrics, thresholds } = trialPeriod;

  const daysProgress = calculatePercentage(
    metrics.daysSinceFirstTransaction,
    thresholds.maxDays,
  );
  const salesProgress = calculatePercentage(
    metrics.completedSalesCount,
    thresholds.maxSalesCount,
  );
  const balanceProgress = calculatePercentage(
    metrics.totalGeneratedBalance.value,
    thresholds.maxGeneratedBalance.value,
  );

  const progressValues = [
    { criterion: 'days' as TrialPeriodCriterion, progress: daysProgress },
    { criterion: 'sales' as TrialPeriodCriterion, progress: salesProgress },
    { criterion: 'balance' as TrialPeriodCriterion, progress: balanceProgress },
  ];

  const maxProgressItem = progressValues.reduce(
    (max, current) => (current.progress > max.progress ? current : max),
    progressValues[0],
  );

  const remaining = {
    days: Math.max(thresholds.maxDays - metrics.daysSinceFirstTransaction, 0),
    sales: Math.max(thresholds.maxSalesCount - metrics.completedSalesCount, 0),
    balance: {
      value: Math.max(
        thresholds.maxGeneratedBalance.value -
          metrics.totalGeneratedBalance.value,
        0,
      ),
      currency: thresholds.maxGeneratedBalance.currency,
    },
  };

  return {
    daysProgress,
    salesProgress,
    balanceProgress,
    maxProgress: maxProgressItem.progress,
    leadingCriterion: maxProgressItem.criterion,
    alertLevel: getAlertLevel(maxProgressItem.progress, blockedTrialPeriod),
    remaining,
  };
};
